import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setImpersonation, clearImpersonation, getImpersonation } from "../lib/api";
import { applyTheme } from "../lib/theme";

const AuthContext = createContext(null);

export const ROLES = [
  { id: "super_admin", label: "Super Admin" },
  { id: "dean", label: "Admissions Dean" },
  { id: "counselor", label: "Counselor" },
  { id: "publisher", label: "Publisher / Partner" },
  { id: "finance", label: "Finance Officer" },
];

// Only these two real, authenticated roles may simulate a DIFFERENT role
// via viewRole (the "Viewing as" switcher) — they're the one admin tier
// this app treats as interchangeable everywhere (ADMIN_ROLES on the
// backend is exactly this pair). Every other real role (counselor,
// publisher, finance) may only ever "view as" itself: Protected
// (App.js) gates every restricted route on viewRole, a purely
// client-side value, so without this a counselor could open the
// switcher and self-select "Super Admin" to reach an admin page's UI
// shell — no real data leaks (every API call still carries the real
// JWT role and the backend re-checks it independently), but the page
// itself shouldn't have been reachable at all. Found 2026-10-18 when a
// counselor account was seen able to open admin-only screens.
export const ADMIN_VIEW_ROLES = ["super_admin", "dean"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [institution, setInstitution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonation, setImp] = useState(() => getImpersonation());
  const [viewRole, setViewRoleState] = useState(() => localStorage.getItem("viewRole") || null);

  const loadInstitution = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/institution");
      setInstitution(data);
      applyTheme({ branding: data.branding, name: `${data.name} CRM` });
      return data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        // Session restore (reload/revisit, not a fresh password login) —
        // a stale viewRole sitting in localStorage from an EARLIER
        // session on this browser/device (e.g. someone previously
        // previewed as "Super Admin" here) must never outlive it for an
        // account that isn't actually allowed to simulate other roles.
        if (!ADMIN_VIEW_ROLES.includes(r.data.role)) {
          setViewRoleState(r.data.role);
          localStorage.setItem("viewRole", r.data.role);
        }
        return loadInstitution();
      })
      .catch(() => setUser(false))
      .finally(() => setLoading(false));
  }, [loadInstitution]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    setViewRoleState(data.role);
    localStorage.setItem("viewRole", data.role);
    loadInstitution();
    return data;
  }, [loadInstitution]);

  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  }, []);

  const setViewRole = useCallback((role) => {
    // Defense in depth, independent of the switcher UI already only
    // offering this to admin-tier accounts (Layout.jsx) — a non-admin
    // real account can only ever set viewRole to its own real role,
    // whatever calls this.
    if (!ADMIN_VIEW_ROLES.includes(user?.role) && role !== user?.role) return;
    setViewRoleState(role);
    localStorage.setItem("viewRole", role);
  }, [user]);

  const enterImpersonation = useCallback(async (payload) => {
    setImpersonation(payload);
    setImp(payload);
    const { data } = await api.get("/auth/me");
    setUser(data);
    setViewRoleState(data.role);
    await loadInstitution();
  }, [loadInstitution]);

  const exitImpersonation = useCallback(async () => {
    clearImpersonation();
    setImp(null);
    const { data } = await api.get("/auth/me");
    setUser(data);
    setViewRoleState(data.role);
    await loadInstitution();
  }, [loadInstitution]);

  // No hardcoded role fallback here on purpose — an undetermined role must
  // never silently resolve to the most-privileged one. In practice this
  // third case (both viewRole and user.role falsy) is only ever hit while
  // still loading, and every Protected route already renders its own
  // loading spinner before this value is ever read for a roles check.
  const effectiveRole = viewRole || (user && user.role) || null;

  return (
    <AuthContext.Provider value={{
      user, institution, loading, login, logout, viewRole: effectiveRole,
      setViewRole, refreshInstitution: loadInstitution,
      impersonation, enterImpersonation, exitImpersonation,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
